"use no memo";

import { PriceSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { Price } from "~/providers/general-provider";
import { usePriceManager } from "~/managers/use-price-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";

export default function PriceForm({
  price,
  onClose,
}: {
  price?: Price;
  onClose: (force?: boolean) => void;
}) {
  const { setUnsavedChanges } = useUnsavedChanges();
  const { createPrice, updatePrice, isLoading } = usePriceManager();
  const { t, i18n } = useTranslation("common");

  const form = useForm({
    schema: PriceSchema(i18n),
    defaultValues: {
      name: price?.name ?? "",
    },
  });
  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (price) {
      const { data, error } = await updatePrice({
        id: price.id,
        ...formData,
      });
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const { data, error } = await createPrice(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  return (
    <Form {...form}>
      <form
        id="price-form"
        className="grid grid-cols-2 gap-2"
        onSubmit={handleSubmit}
      >
        <FormField
          disabled={isLoading}
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Price.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <FormMessage className="col-span-full">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
      </form>
    </Form>
  );
}
